import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useOutletContext, Link, useNavigate, useSearchParams, useLocation} from "react-router-dom";
import {MaterialReactTable, useMaterialReactTable} from "material-react-table";
import useSmartBack from "../../hooks/useSmartBack";

export default function SearchDashboard() {
  const { token, setGlobalLoading } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const smartBack = useSmartBack("/dashboard");
//  const [query, setQuery] = useState("");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [showIntelligenceSearch, setShowIntelligenceSearch] = useState(false);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [intelligenceResults, setIntelligenceResults] = useState(null);

  const [openAccordions, setOpenAccordions] = useState({
    candidates: true,
    roles: false,
    companies: false,
  });

  // ✅ Pagination state
  const [candidatePage, setCandidatePage] = useState(1);
  const [rolePage, setRolePage] = useState(1);
  const perPage = 10;

  useEffect(() => {
      const q = searchParams.get("q") || "";

      setQuery(q);

      if (q.trim()) {
        handleSearch(q);
      }
    }, [searchParams]);
  // ---------------- SEARCH API ----------------

  const handleSearch = async (value) => {
    setQuery(value);

    if (!value.trim()) {
      setResults([]);
      setShowIntelligenceSearch(false);
      setIntelligenceResults(null);
      return;
    }

    const currentQ = searchParams.get("q");

    if (currentQ !== value) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("q", value);
      navigate(
        `/business_intelligence/universalsearch?${updatedParams.toString()}`,
        {
          replace: true,
          state: location.state,
        }
      );
    }

    setGlobalLoading(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/ai_candidates/universal_search`,
        {
          params: { q: value },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Universal Search:", response.data.data);

      if (response.status === 200) {
        setResults(response.data.data || []);

        setCandidatePage(1);
        setRolePage(1);

        // ---------------------------------------------
        // Show Intelligence Search area ONLY after
        // normal search has been executed
        // ---------------------------------------------
        setShowIntelligenceSearch(true);

        // Clear previous intelligence result
        setIntelligenceResults(null);
      }
    } catch (error) {
      console.error("Search error:", error);

      setResults([]);
      setShowIntelligenceSearch(false);
    } finally {
      setGlobalLoading(false);
    }
    };

    const handleIntelligenceSearch = () => {
      const searchValue = query.trim();

      if (!searchValue) {
        return;
      }

      // Store search keyword in localStorage
      localStorage.setItem("searchKeyword",JSON.stringify({keyword: searchValue})
      );

      // Navigate to Intelligence Search page
      navigate("/business_intelligence/new_search_candidates");
    };

  // ---------------- GROUP RESULTS ----------------
  const grouped = useMemo(() => {
    return {
      candidates: results.filter((r) => r.type === "candidate"),
      roles: results.filter((r) => r.type === "role"),
      companies: results.filter((r) => r.type === "company"),
    };
  }, [results]);

  // ---------------- ACCORDION TOGGLE ----------------
  const toggleAccordion = (key) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleBack = () => {
    sessionStorage.removeItem("headerSearch");
    sessionStorage.removeItem("previousPage");
    smartBack();
  };

  const candidateColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row }) => (
          <Link
            to={`/candidates/view/${row.original.id}`}
          >
            {row.original.firstname} {row.original.lastname}
          </Link>
        ),
      },
      {
        accessorKey: "linkdin_url",
        header: "LinkedIn",
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <a
              href={
                cell.getValue().startsWith("http")
                  ? cell.getValue()
                  : `https://${cell.getValue()}`
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa-brands fa-linkedin"></i>
            </a>
          ) : (
            "-"
          ),
      },
      {
        accessorKey: "designation",
        header: "Designation",
      },
      {
        accessorKey: "current_company",
        header: "Company",
      },
      {
        accessorKey: "function_name",
        header: "Function",
      },
    ],
    []
  );

  const candidateTable = useMaterialReactTable({
    columns: candidateColumns,
    data: grouped.candidates,

    enablePagination: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,

    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  const roleColumns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 80,
      },
      {
        accessorKey: "name",
        header: "Role",
        Cell: ({ row }) => (
          <Link
            to={`/workstation/view/${row.original.id}`}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: "company",
        header: "Client",
      },
    ],
    []
  );

  const roleTable = useMaterialReactTable({
    columns: roleColumns,
    data: grouped.roles,

    enablePagination: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,

    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      {/* 🔍 SEARCH BAR */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search candidates, roles, companies..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch(query);
            }
          }}
          style={{
            flex: 1,
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        />

        <button
          onClick={() => handleSearch(query)}
          style={{
            padding: "12px 16px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: "#f37438",
            color: "#fff",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Search
        </button>

        <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center" onClick={handleBack}>
          <i className="ti ti-arrow-left me-1"></i> Back
        </button>
      </div>

        {/* ========= SEARCH WITH INTELLIGENCE =============== */}
        {showIntelligenceSearch && (
          <>
            {/* ================================================
                ZERO CANDIDATES
                Show original centered design
                ================================================ */}

            {grouped.candidates.length === 0 ? (
              <div
                style={{
                  border: "1px solid #dfe5ec",
                  borderRadius: "10px",
                  padding: "35px 15px",
                  marginBottom: "22px",
                  textAlign: "center",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    fontSize: "21px",
                    fontWeight: "600",
                    color: "#111827",
                    marginBottom: "12px",
                  }}
                >
                  No exact matches found for “{query}”.
                </div>

                <div
                  style={{
                    fontSize: "17px",
                    color: "#6b7280",
                    marginBottom: "25px",
                  }}
                >
                  Looking for candidates fitting this brief?
                </div>

                <button
                  onClick={handleIntelligenceSearch}
                  disabled={intelligenceLoading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "14px 26px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#f37438",
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: intelligenceLoading
                      ? "not-allowed"
                      : "pointer",
                    opacity: intelligenceLoading ? 0.7 : 1,
                    minWidth: "280px",
                  }}
                >
                  <i className="ti ti-sparkles"></i>

                  {intelligenceLoading
                    ? "Searching..."
                    : "Search with Intelligence"}

                  {!intelligenceLoading && (
                    <i className="ti ti-arrow-right"></i>
                  )}
                </button>

                <div
                  style={{
                    marginTop: "20px",
                    fontSize: "14px",
                    color: "#64748b",
                  }}
                >
                  We'll turn “{query}” into a role brief and match
                  candidates on skills, industry and seniority.
                </div>
              </div>
            ) : (

              /* ================================================
                CANDIDATES FOUND
                Show new compact design
                ================================================ */

              <div
                style={{
                  border: "1px solid #dfe5ec",
                  borderRadius: "10px",
                  padding: "25px 32px",
                  marginBottom: "22px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "25px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#111827",
                      marginBottom: "8px",
                    }}
                  >
                    Not the right fit? Try Search with Intelligence
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      lineHeight: "1.5",
                    }}
                  >
                    Global search matches names, roles and companies.
                    Intelligence search reads your brief and ranks
                    candidates by skills, industry and seniority —
                    a different way to find talent.
                  </div>
                </div>

                <button
                  onClick={handleIntelligenceSearch}
                  disabled={intelligenceLoading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "10px 18px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#f37438",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: intelligenceLoading
                      ? "not-allowed"
                      : "pointer",
                    opacity: intelligenceLoading ? 0.7 : 1,
                    whiteSpace: "nowrap",
                    minWidth: "280px",
                  }}
                >
                  <i className="ti ti-sparkles"></i>

                  {intelligenceLoading
                    ? "Searching..."
                    : "Search with Intelligence"}

                  {!intelligenceLoading && (
                    <i className="ti ti-arrow-right"></i>
                  )}
                </button>
              </div>
            )}
          </>
        )}

      {/* 📁 ACCORDION */}
      <div style={{ marginBottom: "20px" }}>
        {/* Candidates */}
        <div>
          <div onClick={() => toggleAccordion("candidates")} style={accordionHeader}>
            Candidates ({grouped.candidates.length})
          </div>
          <MaterialReactTable table={candidateTable} />
        </div>

        {/* Roles */}
        <div>
          <div onClick={() => toggleAccordion("roles")} style={accordionHeader}>
            Roles ({grouped.roles.length})
          </div>
          <MaterialReactTable table={roleTable} />
        </div>
      </div>
    </div>
  );
}

/* 🎨 STYLES */
const accordionHeader = {
  padding: "12px",
  background: "#f37438",
  color: "#fff",
  cursor: "pointer",
  marginTop: "10px",
  borderRadius: "6px",
};

const accordionBody = {
  background: "#f9f9f9",
  padding: "10px",
};

const item = {
  padding: "8px",
  borderBottom: "1px solid #eee",
};

const th = {
  textAlign: "left",
  padding: "12px",
};

const td = {
  padding: "12px",
};
